import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import useQuery from "hooks/useQuery";

import {
    OrderDeliveryTypeDto,
    OrderPaymentTypeDto,
    OrderStatusDto,
} from "services/orders/restModels";
import { dateToInputValue, inputValueToDate, startOfDay } from "shared/lib/dateInput";
import {
    ORDER_DELIVERY_TYPE_FILTER_OPTIONS,
    ORDER_PAYMENT_TYPE_FILTER_OPTIONS,
    ORDER_STATUS_FILTER_OPTIONS,
} from "shared/lib/orderLabels";

import { OrdersPaidFilter } from "features/Orders/types";

export interface OrdersFiltersValue {
    menuId: number | null;
    from: Date;
    to: Date;
    statuses: OrderStatusDto[];
    deliveryTypes: OrderDeliveryTypeDto[];
    paymentTypes: OrderPaymentTypeDto[];
    paid: OrdersPaidFilter;
    page: number;
}

export interface OrdersFiltersPatch {
    menuId?: number;
    from?: Date;
    to?: Date;
    statuses?: OrderStatusDto[];
    deliveryTypes?: OrderDeliveryTypeDto[];
    paymentTypes?: OrderPaymentTypeDto[];
    paid?: OrdersPaidFilter;
    page?: number;
}

interface OrdersFiltersQuery {
    menuId?: string;
    from?: string;
    to?: string;
    statuses?: string;
    deliveryTypes?: string;
    paymentTypes?: string;
    paid?: string;
    page?: string;
}

const PAID_QUERY_VALUES: { query: string; paid: boolean }[] = [
    { query: "paid", paid: true },
    { query: "unpaid", paid: false },
];

const parsePaid = (raw: string | undefined): OrdersPaidFilter => {
    const found = PAID_QUERY_VALUES.find((item) => item.query === raw);

    return found === undefined ? null : found.paid;
};

const parseDate = (raw: string | undefined): Date | null =>
    raw === undefined ? null : inputValueToDate(raw);

const parseNumber = (raw: string | undefined): number | null => {
    // Пустое значение отбрасываем до Number: у него пустая строка это 0, и `?menuId=`
    // превратился бы в запрос по несуществующему меню.
    if (raw === undefined || raw.trim() === "") return null;

    const parsed = Number(raw);

    return isNaN(parsed) ? null : parsed;
};

/*
* Из строки оставляем только значения, которые есть в списке фильтра. Иначе правленый руками
* или устаревший URL (`?statuses=Ready`) уходил бы в запрос — а бэк на неизвестное значение
* отвечает 400, и экран показывал бы «не удалось загрузить» вместо очевидного «фильтр пустой».
* Заодно интерфейс и запрос всегда сходятся: в поле не может быть отмечено то, чего нет в списке.
* */
const parseList = <T extends string>(raw: string | undefined, allowed: T[]): T[] => {
    if (raw === undefined) return [];

    const values = raw.split(",").map((item) => item.trim());

    return allowed.filter((option) => values.indexOf(option) >= 0);
};

/*
* Фильтры и номер страницы живут в query, а не в стейте фичи: иначе они умирают вместе с экраном,
* и возврат с карточки заказа открывает список заново за сегодня. Заодно переживают
* перезагрузку, а ссылку на выборку можно отправить.
*
* Пишем через `replace`, как `useActiveTab`: каждая правка фильтра отдельной записью истории
* превратила бы «назад» браузера в отмену фильтров по одному.
* */
export const useOrdersFilters = () => {
    const history = useHistory();
    const query = useQuery<OrdersFiltersQuery>();

    const {
        menuId: rawMenuId,
        from: rawFrom,
        to: rawTo,
        statuses: rawStatuses,
        deliveryTypes: rawDeliveryTypes,
        paymentTypes: rawPaymentTypes,
        paid: rawPaid,
        page: rawPage,
    } = query;

    const value = useMemo<OrdersFiltersValue>(() => {
        const today = startOfDay(new Date());
        const from = parseDate(rawFrom) || today;
        const parsedPage = parseNumber(rawPage);

        return {
            menuId: parseNumber(rawMenuId),
            from,
            // Перевёрнутый период чиним при разборе: бэк на него отвечает 400, а править
            // руками URL никто не обязан аккуратно.
            to: parseDate(rawTo) || from,
            statuses: parseList(rawStatuses, ORDER_STATUS_FILTER_OPTIONS),
            deliveryTypes: parseList(rawDeliveryTypes, ORDER_DELIVERY_TYPE_FILTER_OPTIONS),
            paymentTypes: parseList(rawPaymentTypes, ORDER_PAYMENT_TYPE_FILTER_OPTIONS),
            paid: parsePaid(rawPaid),
            page: parsedPage === null || parsedPage < 1 ? 1 : Math.floor(parsedPage),
        };
    }, [
        rawMenuId,
        rawFrom,
        rawTo,
        rawStatuses,
        rawDeliveryTypes,
        rawPaymentTypes,
        rawPaid,
        rawPage,
    ]);

    const patch = useCallback(
        (next: OrdersFiltersPatch) => {
            const merged = { ...value, ...next };

            const from = merged.from;
            // Границы подтягиваем друг за другом: перевёрнутый период бэк не принимает, а
            // native-поле от него не защищает (дату можно набрать с клавиатуры).
            const to = merged.to.getTime() < from.getTime() ? from : merged.to;

            // Любая правка фильтра возвращает на первую страницу: номер прежней страницы к
            // новой выборке отношения не имеет, а на коротком результате она окажется пустой.
            const page = next.page === undefined ? 1 : merged.page;

            const params = new URLSearchParams();

            if (merged.menuId !== null) params.set("menuId", String(merged.menuId));

            params.set("from", dateToInputValue(from));
            params.set("to", dateToInputValue(to));

            if (merged.statuses.length > 0) params.set("statuses", merged.statuses.join(","));
            if (merged.deliveryTypes.length > 0) {
                params.set("deliveryTypes", merged.deliveryTypes.join(","));
            }
            if (merged.paymentTypes.length > 0) {
                params.set("paymentTypes", merged.paymentTypes.join(","));
            }

            const paidQuery = PAID_QUERY_VALUES.find((item) => item.paid === merged.paid);
            if (paidQuery) params.set("paid", paidQuery.query);

            if (page > 1) params.set("page", String(page));

            history.replace({ search: params.toString() });
        },
        [history, value]
    );

    // Сброс оставляет выбранное меню: это скоуп выборки, а не фильтр по ней.
    const reset = useCallback(() => {
        const params = new URLSearchParams();

        if (value.menuId !== null) params.set("menuId", String(value.menuId));

        history.replace({ search: params.toString() });
    }, [history, value.menuId]);

    return { value, patch, reset };
};
